import { Loader, PanelLeftClose, PanelLeftOpen, SearchX, TriangleAlert } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../admin/session.ts";
import { isApiRequestError, useLakes, useSpot, useSpots } from "../api/index.ts";
import { useLocale } from "../i18n/index.ts";
import { useOnline } from "../pwa/online.ts";
import { Button } from "../components/Button.tsx";
import { Sheet, SheetButton } from "../components/Sheet.tsx";
import type { Detent } from "../components/Sheet.tsx";
import { SpotDetailView } from "./SpotDetailView.tsx";
import { FavouriteButton } from "./FavouriteButton.tsx";
import { SpotHeader } from "./SpotHeader.tsx";

interface Props {
  slug: string;
  detent: Detent;
  onDetentChange: (d: Detent) => void;
  onClose: () => void;
  /** Phones at the large detent: the floating credit is hidden, so show it here. */
  showCredit?: boolean;
  /** Objects outside the 3D tile: flagged in the list. */
  outsideIds?: ReadonlySet<string>;
  view: "2d" | "3d";
  onViewChange: (view: "2d" | "3d") => void;
  /** Desktop 3D only: the panel can fold into a slim strip. */
  panel?: { collapsed: boolean; onToggle: () => void } | undefined;
}

/** The spot detail sheet: loads the spot, shows header from the list while loading, 404 and error states. */
export function SpotSheet({
  slug,
  detent,
  onDetentChange,
  onClose,
  showCredit,
  outsideIds,
  view,
  onViewChange,
  panel,
}: Props) {
  const { t } = useTranslation();
  const { user, staff } = useSession();
  const viewer = user ? (staff ? "staff" : "diver") : "anonymous";
  const locale = useLocale();
  const spotQuery = useSpot(slug);
  const lakes = useLakes();
  const spots = useSpots();
  const titleRef = useRef<HTMLHeadingElement>(null);

  const spot = spotQuery.data;
  const listItem = spots.data?.find((s) => s.slug === slug);
  const lake = spot ? lakes.data?.find((l) => l.slug === spot.lakeSlug) : undefined;
  const online = useOnline();
  // A request that failed on the network while the device is offline: this spot was never saved.
  const unavailableOffline =
    spotQuery.isError &&
    !online &&
    isApiRequestError(spotQuery.error) &&
    spotQuery.error.code === "network";
  const notFound =
    spotQuery.isError && isApiRequestError(spotQuery.error) && spotQuery.error.isNotFound;

  // Move focus into the panel when a spot opens (keyboard and screen-reader users).
  const loadedSlug = spot?.slug;
  useEffect(() => {
    if (loadedSlug) titleRef.current?.focus({ preventScroll: true });
  }, [loadedSlug]);

  const title =
    spot?.name ?? listItem?.name ?? (notFound ? t("sheet.notFoundTitle") : t("common.loading"));

  // the star belongs to published spots (the public list holds only those; staff may open drafts)
  const shown = spot ?? listItem;
  const starSpot = shown?.status === "published" ? shown : undefined;
  const headerAction =
    starSpot || panel ? (
      <>
        {starSpot ? <FavouriteButton spot={starSpot} /> : null}
        {panel ? (
          <SheetButton
            aria-expanded={true}
            aria-label={t("sheet.collapsePanel")}
            icon={<PanelLeftClose size={16} strokeWidth={2} />}
            onClick={panel.onToggle}
          />
        ) : null}
      </>
    ) : undefined;

  if (panel?.collapsed) {
    return (
      <aside className="spot-strip ds-glass" aria-label={title}>
        <button
          type="button"
          className="spot-strip__button"
          aria-expanded={false}
          aria-label={t("sheet.expandPanel")}
          title={t("sheet.expandPanel")}
          onClick={panel.onToggle}
        >
          <span className="sheet-btn__disc" aria-hidden="true">
            <PanelLeftOpen size={16} strokeWidth={2} />
          </span>
          <span className="spot-strip__name ds-text-headline">{title}</span>
        </button>
      </aside>
    );
  }

  return (
    <Sheet
      label={title}
      headerAction={headerAction}
      actionCount={(starSpot ? 1 : 0) + (panel ? 1 : 0)}
      detent={detent}
      onDetentChange={onDetentChange}
      onClose={onClose}
      labels={{
        grabber: t("sheet.grabber"),
        close: t("common.close"),
        detents: {
          peek: t("sheet.detentPeek"),
          medium: t("sheet.detentMedium"),
          large: t("sheet.detentLarge"),
        },
      }}
    >
      {spot ? (
        <SpotDetailView
          key={spot.slug}
          spot={spot}
          lake={lake}
          detent={detent}
          titleRef={titleRef}
          view={view}
          onViewChange={onViewChange}
          outsideIds={outsideIds}
          viewer={viewer}
          editHref={staff ? `/${locale}/admin/spots/${spot.id}` : undefined}
        />
      ) : notFound ? (
        <div className="grid gap-4 justify-items-start" role="alert">
          <SearchX
            aria-hidden="true"
            size={28}
            strokeWidth={1.75}
            className="text-label-secondary"
          />
          <h2 ref={titleRef} tabIndex={-1} className="ds-text-title-1 spot-sheet__title">
            {t("sheet.notFoundTitle")}
          </h2>
          <p className="ds-text-body text-label-secondary m-0">{t("sheet.notFoundText")}</p>
          <Button variant="filled" onClick={onClose}>
            {t("sheet.backToMap")}
          </Button>
        </div>
      ) : spotQuery.isError ? (
        <div className="grid gap-4 justify-items-start" role="alert">
          <TriangleAlert aria-hidden="true" size={28} strokeWidth={1.75} className="text-warning" />
          {unavailableOffline ? (
            <>
              <h2 ref={titleRef} tabIndex={-1} className="ds-text-title-1 spot-sheet__title">
                {t("offline.unavailable")}
              </h2>
              <p className="ds-text-body text-label-secondary m-0">
                {t("offline.unavailableText")}
              </p>
            </>
          ) : (
            <p className="ds-text-body m-0">{t("sheet.loadError")}</p>
          )}
          <Button variant="tinted" onClick={() => void spotQuery.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      ) : (
        <>
          {listItem ? <SpotHeader spot={listItem} detent={detent} titleRef={titleRef} /> : null}
          <p
            className="ds-text-subheadline text-label-secondary m-0 flex items-center gap-2"
            role="status"
          >
            <Loader
              aria-hidden="true"
              size={17}
              strokeWidth={1.75}
              className="animate-spin motion-reduce:animate-none"
            />
            {t("common.loading")}
          </p>
        </>
      )}
      {showCredit ? (
        <p className="spot-sheet__credit ds-text-caption-1">{t("map.attribution")}</p>
      ) : null}
    </Sheet>
  );
}
